import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { X, ChevronRight, ChevronLeft, User2, MapPin, FileText, Briefcase, Package } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Label } from '../../ui/label';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel } from '../../ui/select';
import { FileInput } from './FormComponents/FileInput';
import { MultiSelect } from './FormComponents/MultiSelect';
import { PackageInput } from './FormComponents/PackageInput';
import { LocationPicker } from '../../LocationPicker';
import { categories, categoryGroups } from '../../../lib/constants/categories';

import { languages } from '../../../lib/constants/languages';
import { wellnessConcerns } from '../../../lib/constants/wellnessConcerns';
import { workArrangements } from '../../../lib/constants';
import { supabase } from '../../../lib/supabase';
import type { PractitionerFormData } from '../../../types/listings';

interface PractitionerFormProps {
  onClose: () => void;
  onSuccess: () => void;
  editId?: string;
}

type Step = 'basics' | 'location' | 'packages' | 'services' | 'media';

interface StepConfig {
  id: Step;
  title: string;
  icon: React.ComponentType<any>;
}

const steps: StepConfig[] = [
  { id: 'basics', title: 'Basic Info', icon: User2 },
  { id: 'location', title: 'Location', icon: MapPin },
  { id: 'packages', title: 'Packages', icon: Package },
  { id: 'services', title: 'Services', icon: Briefcase },
  { id: 'media', title: 'Media', icon: FileText },
];

export function PractitionerForm({ onClose, onSuccess, editId }: PractitionerFormProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState<Step>('basics');
  const [formData, setFormData] = useState<PractitionerFormData & { latitude?: number | null; longitude?: number | null }>({
    category: '',
    title: '',
    description: '',
    work_arrangement: '',
    corporate_wellness: false,
    booking_available: true,
    country: '',
    address: '',
    languages: [],
    helps_with: [],
    currency: 'EUR',
    faqs: '',
    packages: [],
    latitude: null,
    longitude: null
  });
  const [images, setImages] = useState<File[]>([]);
  const [certification, setCertification] = useState<File | null>(null);
  const [existingImages, setExistingImages] = useState<string[]>([]);
  const [existingCertificationUrl, setExistingCertificationUrl] = useState<string>('');
  const [loading, setLoading] = useState(!!editId);
  const [error, setError] = useState('');

  // Fetch practitioner data if editing
  useEffect(() => {
    if (editId) {
      const fetchPractitioner = async () => {
        setLoading(true);
        try {
          const { data, error } = await supabase
            .from('practitioners')
            .select('*')
            .eq('id', editId)
            .single();

          if (error) throw error;
          if (data) {
            // Parse price list to extract hourly rate and currency
            let hourlyRate = '';
            let currency = 'EUR';
            
            if (data.price_list) {
              const priceMatch = data.price_list.match(/(\d+(?:\.\d+)?)\s*([A-Z]{3}|[€$£])/i);
              if (priceMatch) {
                hourlyRate = priceMatch[1];
                const currencyMatch = priceMatch[2].toUpperCase();
                // Convert symbols to currency codes
                if (currencyMatch === '€') currency = 'EUR';
                else if (currencyMatch === '$') currency = 'USD';
                else if (currencyMatch === '£') currency = 'GBP';
                else currency = currencyMatch;
              }
            }

            // Fetch existing packages
            const { data: packagesData, error: packagesError } = await supabase
              .from('practitioner_packages')
              .select('*')
              .eq('practitioner_id', data.id)
              .order('price', { ascending: true });

            if (packagesError) throw packagesError;

            const newFormData = {
              category: data.category || '',
              title: data.title || '',
              description: data.description || '',
              work_arrangement: data.work_arrangement || '',
              corporate_wellness: data.corporate_wellness || false,
              booking_available: data.booking_available ?? true,
              country: data.country || '',
              address: data.address || '',
              languages: data.language ? data.language.split(',').map(lang => lang.trim()) : [],
              helps_with: data.helps_with ? data.helps_with.split(',').map(t => t.trim()) : [],
              hourly_rate: hourlyRate,
              currency: currency,
              faqs: data.faqs || '',
              packages: packagesData ? packagesData.map(pkg => ({
                name: pkg.name,
                description: pkg.description,
                price: pkg.price,
                features: pkg.features || []
              })) : [],
              latitude: data.latitude || null,
              longitude: data.longitude || null
            };
            
            setFormData(newFormData);
            
            // Set existing images and certification
            if (data.images && data.images.length > 0) {
              setExistingImages(data.images);
            }
            if (data.certification_url) {
              setExistingCertificationUrl(data.certification_url);
            }
          }
        } catch (err) {
          console.error('Error fetching practitioner:', err);
          setError('Failed to load practitioner data');
        } finally {
          setLoading(false);
        }
      };

      fetchPractitioner();
    }
  }, [editId]);

  const handleSubmit = async () => {
    setLoading(true);
    setError('');

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Upload images if any
      const imageUrls = await Promise.all(
        images.map(async (file) => {
          const { data, error } = await supabase.storage
            .from('listing-images')
            .upload(`${user.id}/${Date.now()}-${file.name}`, file);
          
          if (error) throw error;
          
          const { data: { publicUrl } } = supabase.storage
            .from('listing-images')
            .getPublicUrl(data.path);
          
          return publicUrl;
        })
      );

      // Upload certification if provided
      let certificationUrl = '';
      if (certification) {
        const { data: certData, error: certError } = await supabase.storage
          .from('certification-images')
          .upload(`${user.id}/${Date.now()}-${certification.name}`, certification);
        
        if (certError) throw certError;
        
        const { data: { publicUrl } } = supabase.storage
          .from('certification-images')
          .getPublicUrl(certData.path);
        
        certificationUrl = publicUrl;
      } else if (existingCertificationUrl) {
        // Keep existing certification if no new one uploaded
        certificationUrl = existingCertificationUrl;
      }

      // Find the lowest package price for starting_price
      const startingPrice = Math.min(...formData.packages.map(pkg => pkg.price));

      // Prepare data for insert/update
      const practitionerData = {
          user_id: user.id,
          category: formData.category,
          title: formData.title,
          description: formData.description,
          work_arrangement: formData.work_arrangement,
          corporate_wellness: formData.corporate_wellness,
          booking_available: formData.booking_available ?? true,
          country: formData.country,
          address: formData.address,
          language: formData.languages.join(','),
          helps_with: formData.helps_with.join(','),
          starting_price: startingPrice,
          currency: formData.currency,
          faqs: formData.faqs,
          images: [...existingImages, ...imageUrls],
          certification_url: certificationUrl,
          latitude: formData.latitude,
          longitude: formData.longitude
      };

      let error;
      
      if (editId) {
        // Update existing practitioner
        const { error: updateError } = await supabase
          .from('practitioners')
          .update(practitionerData)
          .eq('id', editId);
        
        error = updateError;
      } else {
        // Create new practitioner
        const { error: insertError } = await supabase
          .from('practitioners')
          .insert([practitionerData]);
        
        error = insertError;
      }

      if (error) throw error;

      // Get the practitioner ID
      const practitionerId = editId || (await supabase
        .from('practitioners')
        .select('id')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .single()
      ).data?.id;

      if (!practitionerId) throw new Error('Failed to get practitioner ID');

      // Delete existing packages if editing
      if (editId) {
        const { error: deleteError } = await supabase
          .from('practitioner_packages')
          .delete()
          .eq('practitioner_id', practitionerId);
        
        if (deleteError) throw deleteError;
      }

      // Insert new packages
      const { error: packagesError } = await supabase
        .from('practitioner_packages')
        .insert(formData.packages.map(pkg => ({
          practitioner_id: practitionerId,
          name: pkg.name,
          description: pkg.description,
          price: typeof pkg.price === 'string' ? parseFloat(pkg.price) : pkg.price,
          currency: formData.currency,
          features: pkg.features
        })));

      if (packagesError) throw packagesError;
      
      // If bookings are enabled, guide practitioner to set working hours.
      if ((formData.booking_available ?? true) && practitionerId) {
        const { data: whData, error: whError } = await supabase
          .from('practitioner_working_hours')
          .select('day_of_week', { count: 'exact' })
          .eq('practitioner_id', practitionerId)
          .limit(1);
        // If query fails, don't block saving the listing.
        if (!whError && (!whData || whData.length === 0)) {
          // Close the modal first, then send them to booking setup.
          onSuccess();
          onClose();
          navigate(`/bookings/${practitionerId}?tab=working-hours`);
          return;
        }
      }
      onSuccess();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const selectedCategory = categories.find(c => c.value === formData.category);

  const isStepValid = () => {
    switch (currentStep) {
      case 'basics':
        return formData.category && formData.title && formData.description;
      case 'location':
        return formData.country && formData.work_arrangement;
      case 'packages':
        return formData.packages.every(pkg => 
          pkg.name && pkg.description && pkg.price !== '' && pkg.price > 0 && pkg.features.length > 0 && pkg.features.every(f => f.trim() !== '')
        );
      case 'services':
        return formData.languages.length > 0;
      case 'media':
        return true; // Media is optional
    }
  };

  const handleNext = () => {
    if (!isStepValid()) {
      setError('Please fill in all required fields');
      return;
    }
    
    const currentIndex = steps.findIndex(s => s.id === currentStep);
    if (currentIndex < steps.length - 1) {
      setCurrentStep(steps[currentIndex + 1].id);
    } else {
      handleSubmit();
    }
    setError('');
  };

  const handleBack = () => {
    const currentIndex = steps.findIndex(s => s.id === currentStep);
    if (currentIndex > 0) {
      setCurrentStep(steps[currentIndex - 1].id);
    }
    setError('');
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 'basics':
        return (
          <div className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="category">Category *</Label>
              <Select
                value={formData.category}
                onValueChange={(value) => setFormData(prev => ({ ...prev, category: value }))}
              >
                <SelectTrigger id="category">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {categoryGroups.map((group) => (
                    <SelectGroup key={group}>
                      <SelectLabel className="font-semibold text-accent-text px-2">{group}</SelectLabel>
                      {categories.filter(cat => cat.group === group).map((cat) => (
                        <SelectItem key={cat.value} value={cat.value}>
                          {cat.label}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="title">Title *</Label>
              <Input
                id="title"
                type="text"
                required
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                placeholder="e.g., Certified Yoga Instructor"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description *</Label>
              <Textarea
                id="description"
                required
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Describe your services..."
                rows={5}
              />
            </div>
          </div>
        );

      case 'location':
        return (
          <div className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="work-arrangement">Work Arrangement *</Label>
              <Select
                value={formData.work_arrangement}
                onValueChange={(value) => setFormData(prev => ({ ...prev, work_arrangement: value }))}
              >
                <SelectTrigger id="work-arrangement">
                  <SelectValue placeholder="Select work arrangement" />
                </SelectTrigger>
                <SelectContent>
                  {workArrangements.map((arr) => (
                    <SelectItem key={arr.value} value={arr.value}>
                      {arr.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center space-x-2">
              <input
                id="corporate-wellness"
                type="checkbox"
                checked={formData.corporate_wellness}
                onChange={(e) => setFormData(prev => ({ ...prev, corporate_wellness: e.target.checked }))}
                className="rounded border-gray-300 text-accent-text focus:ring-accent-text h-4 w-4"
              />
              <Label htmlFor="corporate-wellness" className="cursor-pointer font-normal">
                {t('forms.practitioner.corporateWellness')}
              </Label>
            </div>

            <div className="border border-gray-200 rounded-xl p-4 bg-gray-50 space-y-3">
              <div className="flex items-start gap-3">
                <input
                  id="booking-available"
                  type="checkbox"
                  checked={!!formData.booking_available}
                  onChange={(e) => setFormData(prev => ({ ...prev, booking_available: e.target.checked }))}
                  className="mt-1 rounded border-gray-300 text-accent-text focus:ring-accent-text h-4 w-4"
                />
                <div className="min-w-0">
                  <Label htmlFor="booking-available" className="cursor-pointer">
                    Use Mindful Family booking system
                  </Label>
                  <p className="text-sm text-content/60 mt-0.5">
                    Enable easy in-platform bookings. If disabled, users can still message you to arrange sessions directly.
                  </p>
                </div>
              </div>
              {formData.booking_available ? (
                <p className="text-sm text-content/60">
                  After saving, you’ll be prompted to set your working hours and availability.
                </p>
              ) : null}
            </div>

            <LocationPicker
              latitude={formData.latitude}
              longitude={formData.longitude}
              onChange={(lat, lng, address, countryCode) => setFormData(prev => ({
                ...prev,
                latitude: lat,
                longitude: lng,
                address: address || prev.address,
                country: countryCode || prev.country
              }))}
              label={t('forms.practitioner.yourLocation')}
              helperText={t('forms.practitioner.searchAddress')}
            />
          </div>
        );

      case 'packages':
        return (
          <div className="space-y-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-content">{t('forms.practitioner.servicePackages')}</h3>
              <div className="flex items-center space-x-2">
                <label className="text-sm text-content/80">{t('forms.practitioner.currency')}</label>
                <select
                  value={formData.currency}
                  onChange={(e) => setFormData(prev => ({ ...prev, currency: e.target.value }))}
                  className="px-2 py-1 border border-accent-text/20 rounded-lg focus:border-accent-text focus:ring-1 focus:ring-accent-text/20"
                >
                  <option value="EUR">EUR (€)</option>
                  <option value="USD">USD ($)</option>
                  <option value="GBP">GBP (£)</option>
                </select>
              </div>
            </div>
            <PackageInput
              packages={formData.packages}
              onChange={(packages) => setFormData(prev => ({ ...prev, packages }))}
              currency={formData.currency}
            />
          </div>
        );

      case 'services':
        return (
          <div className="space-y-6">
            <MultiSelect
              label={t('forms.practitioner.languages')}
              options={languages}
              selectedValues={formData.languages}
              onChange={(values) => setFormData(prev => ({ ...prev, languages: values }))}
              required
            />

            <MultiSelect
              label="What do you help with?"
              options={wellnessConcerns}
              selectedValues={formData.helps_with}
              onChange={(values) => setFormData(prev => ({ ...prev, helps_with: values }))}
              placeholder="Select what you help people with"
            />

            <div className="space-y-2">
              <Label htmlFor="faqs">{t('forms.practitioner.faqs')}</Label>
              <Textarea
                id="faqs"
                value={formData.faqs}
                onChange={(e) => setFormData(prev => ({ ...prev, faqs: e.target.value }))}
                placeholder={t('forms.practitioner.faqsPlaceholder')}
                rows={4}
              />
            </div>
          </div>
        );

      case 'media':
        return (
          <div className="space-y-6">
            <FileInput
              label={t('forms.practitioner.images')}
              onChange={setImages}
              maxFiles={5}
              accept="image/*"
              multiple
              description={t('forms.practitioner.uploadImages')}
            />

            {existingImages.length > 0 && (
              <div>
                <h3 className="text-sm font-medium text-content mb-2">{t('forms.practitioner.existingImages')}</h3>
                <div className="grid grid-cols-3 gap-2">
                  {existingImages.map((url, index) => (
                    <div key={index} className="relative">
                      <img 
                        src={url} 
                        alt={`Practitioner image ${index + 1}`} 
                        className="w-full h-24 object-cover rounded-lg"
                      />
                      <button
                        type="button"
                        onClick={() => setExistingImages(prev => prev.filter((_, i) => i !== index))}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {selectedCategory?.requiresCertification && (
              <FileInput
                label={t('forms.practitioner.certification')}
                onChange={(files) => setCertification(files[0])}
                maxFiles={1}
                accept="image/*,application/pdf"
                description={t('forms.practitioner.certificationDesc')}
                required
              />
            )}
          </div>
        );
    }
  };

  const getStepTitle = () => {
    switch (currentStep) {
      case 'basics': return t('forms.practitioner.basicInformation');
      case 'location': return t('forms.practitioner.locationWorkType');
      case 'packages': return t('forms.practitioner.servicePackages');
      case 'services': return t('forms.practitioner.servicesLanguages');
      case 'media': return t('forms.practitioner.mediaCertification');
      default: return '';
    }
  };

  const currentStepIndex = steps.findIndex(s => s.id === currentStep);

  const modalOverlayClass = "fixed inset-0 min-h-[100dvh] bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-0 lg:p-4";
  const modalInnerClass = "bg-background rounded-none lg:rounded-lg w-full h-full min-h-[100dvh] lg:min-h-0 lg:h-auto lg:max-w-2xl lg:max-h-[90vh] overflow-hidden flex flex-col lg:shadow-2xl";

  if (loading) {
    const content = (
      <div className={modalOverlayClass}>
        <div className={modalInnerClass}>
          <div className="flex-1 flex items-center justify-center">
            <div className="flex items-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-text"></div>
              <span className="ml-3 text-content">{t('forms.practitioner.loadingData')}</span>
            </div>
          </div>
        </div>
      </div>
    );
    return typeof document !== 'undefined' ? createPortal(content, document.body) : content;
  }

  const content = (
    <div className={modalOverlayClass}>
      <div className={modalInnerClass}>
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-semibold text-content">
              {getStepTitle()}
            </h2>
            <p className="text-sm text-content/60 mt-1">{t('forms.common.stepOf', { current: currentStepIndex + 1, total: steps.length })}</p>
          </div>
          <button onClick={onClose} className="text-content/60 hover:text-content">
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Progress Bar */}
        <div className="px-6 py-4 bg-gray-50">
          <div className="flex gap-2">
            {steps.map((_, index) => (
              <div
                key={index}
                className={`flex-1 h-2 rounded-full transition-colors ${
                  index <= currentStepIndex ? 'bg-accent-text' : 'bg-gray-200'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-md text-sm">{error}</div>
          )}

          {renderStepContent()}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 p-6 flex justify-between items-center">
          <button
            type="button"
            onClick={handleBack}
            disabled={currentStep === 'basics'}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
              currentStep === 'basics'
                ? 'text-gray-400 cursor-not-allowed'
                : 'text-content hover:bg-gray-100'
            }`}
          >
            <ChevronLeft className="w-4 h-4" />
            {t('forms.common.previous')}
          </button>

          {currentStep !== 'media' ? (
            <button
              type="button"
              onClick={handleNext}
              disabled={!isStepValid()}
              className={`flex items-center gap-2 px-6 py-2 rounded-full font-medium text-white transition-colors ${
                isStepValid()
                  ? 'bg-accent-text hover:bg-accent-text/90'
                  : 'bg-gray-300 cursor-not-allowed'
              }`}
            >
              {t('forms.common.next')}
              <ChevronRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              type="button"
              onClick={handleNext}
              disabled={loading || !isStepValid()}
              className={`flex items-center gap-2 px-6 py-2 rounded-full font-medium text-white transition-colors ${
                loading || !isStepValid()
                  ? 'bg-gray-300 cursor-not-allowed'
                  : 'bg-accent-text hover:bg-accent-text/90'
              }`}
            >
              {loading ? (editId ? t('forms.common.updating') : t('forms.common.creating')) : (editId ? t('forms.practitioner.updateListing') : t('forms.practitioner.createListing'))}
            </button>
          )}
        </div>
      </div>
    </div>
  );
  return typeof document !== 'undefined' ? createPortal(content, document.body) : content;
}